import React, { useState, useEffect } from 'react';
import api from '../api';
import ProviderForm from '../components/ProviderForm';
import ProviderTable from '../components/ProviderTable';
import ModelTester from '../components/ModelTester';

function getDefaultForm() {
  return {
    name: '',
    base_url: '',
    api_key: '',
    alias: '',
    request_limit: 0,
    token_limit: 0,
    reset_period_hours: 24,
    priority: 0,
    testedModels: [],
  };
}

function Providers() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(getDefaultForm());
  const [testingProvider, setTestingProvider] = useState(null);

  useEffect(() => {
    loadProviders();
  }, []);

  const loadProviders = async () => {
    try {
      const data = await api.getProviders();
      setProviders(data.providers);
    } catch (err) {
      console.error('Failed to load providers:', err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData(getDefaultForm());
    setEditing(null);
    setShowForm(false);
  };

  const handleEdit = (provider) => {
    setFormData({
      name: provider.name,
      base_url: provider.base_url,
      api_key: '',
      alias: provider.alias || '',
      request_limit: provider.request_limit,
      token_limit: provider.token_limit,
      reset_period_hours: provider.reset_period_hours,
      priority: provider.priority,
      testedModels: [],
    });
    setEditing(provider.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.updateProvider(editing, formData);
      } else {
        await api.addProvider(formData);
      }
      resetForm();
      loadProviders();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure?')) return;
    try {
      await api.deleteProvider(id);
      loadProviders();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleReset = async (id) => {
    try {
      await api.resetProvider(id);
      loadProviders();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleTestModels = (provider) => {
    setTestingProvider(testingProvider?.id === provider.id ? null : provider);
  };

  if (loading) return <div className="loading">Loading providers...</div>;

  return (
    <div className="providers-page">
      <div className="page-header">
        <h1>Providers</h1>
        <button onClick={() => setShowForm(true)} className="btn btn-primary">+ Add Provider</button>
      </div>

      {showForm && (
        <div className="card form-card">
          <h2>{editing ? 'Edit Provider' : 'Add Provider'}</h2>
          <ProviderForm
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleSubmit}
            onCancel={resetForm}
            editing={editing}
          />
        </div>
      )}

      <div className="card">
        {providers.length === 0 ? (
          <div className="empty-state">
            <p>No providers configured yet. Add your first free API provider!</p>
          </div>
        ) : (
          <ProviderTable
            providers={providers}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onReset={handleReset}
            onTestModels={handleTestModels}
          />
        )}
      </div>

      {testingProvider && (
        <div className="card">
          <h2>Test Models — {testingProvider.name}</h2>
          <ModelTester
            baseUrl={testingProvider.base_url}
            apiKey={testingProvider.api_key}
          />
        </div>
      )}
    </div>
  );
}

export default Providers;
